import { useState, useEffect, useCallback } from 'react';
import { ref, onValue, runTransaction, set } from 'firebase/database';
import { db } from '../services/firebase';
import { User } from 'firebase/auth';

// Format large coin amounts
function formatCoins(amount: number): string {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toString();
}

// Local storage helpers
function getLocalCoins(): number {
  try {
    const raw = localStorage.getItem('sbarazzo_coins');
    return raw ? parseInt(raw, 10) : 0;
  } catch { return 0; }
}

function setLocalCoins(amount: number) {
  localStorage.setItem('sbarazzo_coins', amount.toString());
}

interface UseCoinsReturn {
  balance: number;
  loading: boolean;
  earnCoins: (amount: number, reason: string) => Promise<void>;
  spendCoins: (amount: number, reason: string) => Promise<boolean>;
  hasEnough: (amount: number) => boolean;
}

export function useCoins(user: User | null): UseCoinsReturn {
  const [balance, setBalance] = useState<number>(getLocalCoins);
  const [loading, setLoading] = useState(true);
  const [localOverride, setLocalOverride] = useState(false);

  useEffect(() => {
    if (!user) {
      setBalance(getLocalCoins());
      setLoading(false);
      return;
    }

    const coinsRef = ref(db, `users/${user.uid}/coins/balance`);
    const unsub = onValue(coinsRef, (snapshot) => {
      const val = snapshot.val();
      if (val !== null) {
        setBalance(val);
        setLocalCoins(val);
        setLocalOverride(false);
      } else {
        // Initialize with 0
        set(ref(db, `users/${user.uid}/coins`), {
          balance: 0,
          lifetimeEarned: 0,
          lifetimeSpent: 0,
        });
        setBalance(0);
        setLocalCoins(0);
      }
      setLoading(false);
    }, (err) => {
      console.warn('[Coins] Firebase read error, falling back to local:', err.message);
      setBalance(getLocalCoins());
      setLocalOverride(true);
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  const earnCoins = useCallback(async (amount: number, reason: string) => {
    if (amount <= 0) return;
    const newBalance = balance + amount;
    setBalance(newBalance);
    setLocalCoins(newBalance);

    if (user && !localOverride) {
      const coinRef = ref(db, `users/${user.uid}/coins/balance`);
      await runTransaction(coinRef, (current) => ((current || 0) + amount));
    }
    console.log(`[Coins] +${amount} (${reason}) — balance: ${newBalance}`);
  }, [balance, user, localOverride]);

  const spendCoins = useCallback(async (amount: number, reason: string): Promise<boolean> => {
    if (amount <= 0) return false;
    if (balance < amount) return false;
    const newBalance = balance - amount;
    setBalance(newBalance);
    setLocalCoins(newBalance);

    if (user && !localOverride) {
      const coinRef = ref(db, `users/${user.uid}/coins/balance`);
      await runTransaction(coinRef, (current) => Math.max(0, (current || 0) - amount));
    }
    console.log(`[Coins] -${amount} (${reason}) — balance: ${newBalance}`);
    return true;
  }, [balance, user, localOverride]);

  const hasEnough = useCallback((amount: number) => balance >= amount, [balance]);

  return { balance, loading, earnCoins, spendCoins, hasEnough };
}

export { formatCoins };